import { useEffect, useRef, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '@/lib/db';

const DISMISS_KEY = 'jnd:android-install-dismissed';
const COOLDOWN_DAYS = 30;
const CHAPTERS_KEY = 'jnd:chapters-read';
const SESSIONS_KEY = 'jnd:sessions-count';
const MIN_CHAPTERS = 3;
const MIN_SESSIONS = 2;

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return window.matchMedia('(display-mode: standalone)').matches || (navigator as any).standalone === true;
}

function loadDismissedAt(): number {
  try {
    const v = localStorage.getItem(DISMISS_KEY);
    return v ? Number(v) : 0;
  } catch {
    return 0;
  }
}

function readCount(key: string): number {
  try {
    return Number(localStorage.getItem(key) ?? '0');
  } catch {
    return 0;
  }
}

function bumpHeuristics(): { chapters: number; sessions: number } {
  let chapters = readCount(CHAPTERS_KEY);
  let sessions = readCount(SESSIONS_KEY);
  try {
    chapters += 1;
    localStorage.setItem(CHAPTERS_KEY, String(chapters));
    // Treat a "session" as the first chapter loaded in a new tab/visit
    if (!sessionStorage.getItem('jnd:session-counted')) {
      sessions += 1;
      localStorage.setItem(SESSIONS_KEY, String(sessions));
      sessionStorage.setItem('jnd:session-counted', '1');
    }
  } catch {
    /* ignore */
  }
  return { chapters, sessions };
}

export function InstallPromptAndroid() {
  const [event, setEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [show, setShow] = useState(false);
  const prefs = useLiveQuery(() => db.prefs.get('singleton'), [], undefined);
  const bumped = useRef(false);

  // Bump heuristics on first mount of any chapter view.
  useEffect(() => {
    if (bumped.current) return;
    bumped.current = true;
    bumpHeuristics();
  }, []);

  useEffect(() => {
    if (isStandalone()) return;

    const onBeforeInstall = (e: Event) => {
      e.preventDefault();
      setEvent(e as BeforeInstallPromptEvent);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    return () => window.removeEventListener('beforeinstallprompt', onBeforeInstall);
  }, []);

  useEffect(() => {
    if (!event) return;
    const dismissedAt = loadDismissedAt();
    const cooldown = COOLDOWN_DAYS * 24 * 60 * 60 * 1000;
    if (Date.now() - dismissedAt < cooldown) return;
    if (prefs?.installedDismissedAt && Date.now() - prefs.installedDismissedAt < cooldown) return;
    const chapters = readCount(CHAPTERS_KEY);
    const sessions = readCount(SESSIONS_KEY);
    if (chapters >= MIN_CHAPTERS && sessions >= MIN_SESSIONS) {
      setShow(true);
    }
  }, [event, prefs?.installedDismissedAt]);

  if (!show || !event) return null;

  const install = async () => {
    setShow(false);
    try {
      await event.prompt();
      await event.userChoice;
    } catch {
      /* user cancelled */
    }
    setEvent(null);
  };

  const dismiss = async () => {
    setShow(false);
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* ignore */
    }
    if (prefs) {
      await db.prefs.put({ ...prefs, installedDismissedAt: Date.now() });
    }
  };

  return (
    <div className="install-banner" role="dialog" aria-labelledby="aos-install-title">
      <h2 id="aos-install-title" style={{ marginTop: 0, fontSize: '1rem' }}>
        Install JND Bible
      </h2>
      <p className="muted" style={{ margin: '.5rem 0', fontSize: '.85rem' }}>
        Add to your home screen for a quiet, full-screen reader that works offline.
      </p>
      <div style={{ display: 'flex', gap: '.5rem', justifyContent: 'flex-end' }}>
        <button className="btn-secondary" onClick={dismiss}>
          Not now
        </button>
        <button className="btn-primary" onClick={install}>
          Install
        </button>
      </div>
    </div>
  );
}
